import { useEffect, useRef, useCallback } from 'react';
import { useGlobalContext } from '~/context/GlobalContext';
import { IWebRTCData } from '~/types/common';
import { toast, Bounce } from 'react-toastify';

const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: process.env.REACT_APP_STUN_SERVER || "" },
    {
      urls: [
        process.env.REACT_APP_TURN_SERVER_TCP || "",
        process.env.REACT_APP_TURN_SERVER_UDP || ""
      ],
      username: 'turn',
      credential: process.env.REACT_APP_TURN_CREDENTIAL || "",
    },
  ],
};

// Funkcja pomocnicza do ujednoliconego logowania błędów na czerwono (10 sekund)
const logErrorToast = (prefix: string, err: any) => {
  console.error(`${prefix}`, err);
  const message = err instanceof Error ? err.message : String(err);
  toast.error(`${prefix} ${message}`, {
    position: "bottom-center",
    autoClose: 10000, 
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "colored", 
    transition: Bounce,
  });
};

export const useWebRTC = () => {
  const { context, setContext } = useGlobalContext();
  const pc = useRef<RTCPeerConnection | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  const currentPartnerId = useRef<string | null>(null);

  // Niezależna referencja do kamery (odporna na re-rendery Reacta)
  const activeLocalStream = useRef<MediaStream | null>(null);

  // --- KULOODPORNE SPRZĄTANIE ZASOBÓW ---
  const cleanup = useCallback(() => {
    try {
      console.log("[WebRTC] Trwa twarde sprzątanie połączenia i zasobów...");

      // Zatrzymanie sprzętowe kamery (gaśnie dioda)
      if (activeLocalStream.current) {
        activeLocalStream.current.getTracks().forEach(track => track.stop());
        activeLocalStream.current = null;
      }

      // Zamknięcie połączenia P2P
      if (pc.current) {
        pc.current.onicecandidate = null;
        pc.current.ontrack = null;
        pc.current.oniceconnectionstatechange = null;
        pc.current.onconnectionstatechange = null;
        pc.current.onsignalingstatechange = null;
        pc.current.close();
        pc.current = null;
      }

      pendingCandidates.current = [];
      currentPartnerId.current = null;

      // Reset strumieni w UI
      setContext(prev => ({ ...prev, remoteStream: null, localStream: null, callSession: null }));
    } catch (err) {
      logErrorToast("[WebRTC] Błąd podczas czyszczenia zasobów:", err);
    }
  }, [setContext]);

  const emitSignal = (event: string, partnerId: string, sessionData: any) => {
    try {
      const data: IWebRTCData = {
        event,
        initiatorId: context.user?.id || '',
        partnerId: partnerId,
        roomId: context.room?.id || '',
        session: sessionData,
      };
      window.dispatchEvent(new CustomEvent('webRTC', { detail: data }));
    } catch (err) {
      logErrorToast(`[WebRTC] Błąd emisji sygnału (${event}):`, err);
    }
  };

  const initPeerConnection = (partnerId: string) => {
    try {
      if (pc.current) pc.current.close();

      currentPartnerId.current = partnerId;
      const peer = new RTCPeerConnection(ICE_CONFIG);

      peer.onicecandidate = (event) => {
        try {
          if (event.candidate) emitSignal('webRtcCandidate', partnerId, event.candidate);
        } catch (err) {
          logErrorToast("[WebRTC] Błąd podczas przetwarzania lokalnego kandydata ICE:", err);
        }
      };

      peer.ontrack = (event) => {
        try {
          if (event.streams && event.streams[0]) {
            console.log("[WebRTC] Otrzymano strumień zdalny!");
            setContext(prev => ({ ...prev, remoteStream: event.streams[0] }));
          }
        } catch (err) {
          logErrorToast("[WebRTC] Błąd podczas przypisywania zdalnego strumienia:", err);
        }
      };

      // Sprawdzanie stanu ICE (Routing pakietów)
      peer.oniceconnectionstatechange = () => {
        if (!currentPartnerId.current) return;
        if (peer.iceConnectionState === 'disconnected' || peer.iceConnectionState === 'failed') {
          const msg = `Utracono połączenie sieciowe z partnerem (stan ICE: ${peer.iceConnectionState}).`;
          logErrorToast("Połączenie przerwane:", new Error(msg));
          cleanup();
          setContext(prev => ({ ...prev, callSession: null }));
          window.dispatchEvent(new CustomEvent('cancelCall', { detail: { event: 'cancelCall', partnerId } }));
        }
      };

      // Sprawdzanie głównego stanu P2P (DTLS, Szyfrowanie, Połączenie)
      peer.onconnectionstatechange = () => {
        if (!currentPartnerId.current) return;
        if (peer.connectionState === 'failed') {
          logErrorToast("Błąd WebRTC:", new Error("Nadrzędne połączenie P2P zakończyło się statusem 'failed'."));
          cleanup();
          setContext(prev => ({ ...prev, callSession: null }));
          window.dispatchEvent(new CustomEvent('cancelCall', { detail: { event: 'cancelCall', partnerId } }));
        }
      };

      // Sprawdzanie stanu negocjacji (SDP Offer/Answer)
      peer.onsignalingstatechange = () => {
        if (peer.signalingState === 'closed' && currentPartnerId.current) {
          console.warn("[WebRTC] Połączenie WebRTC zostało nagle zamknięte (signalingState: closed).");
        }
      };

      pc.current = peer;
      return peer;
    } catch (err) {
      logErrorToast("[WebRTC] Błąd inicjalizacji RTCPeerConnection:", err);
      throw err;
    }
  };

  const startCall = async () => {
    // Zabezpieczenie: Jeśli już dzwonimy/rozmawiamy, zignoruj kolejne kliknięcia
    if (pc.current && pc.current.signalingState !== 'closed') {
      console.warn("[WebRTC] Łączenie już trwa, ignoruję żądanie.");
      return;
    }

    try {
      let stream = activeLocalStream.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
        activeLocalStream.current = stream;
        setContext(prev => ({ ...prev, localStream: stream }));
      }

      const partnerId = context.callSession?.initiatorId;

      if (!partnerId) {
        throw new Error("Brak ID partnera w sesji (context.callSession.initiatorId). Przerywam łączenie.");
      }

      const peer = initPeerConnection(partnerId);
      stream.getTracks().forEach(track => peer.addTrack(track, stream!));

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      emitSignal('webRtcOffer', partnerId, offer);

    } catch (err: any) {
      // Bardziej precyzyjne logowanie błędów kamery
      let friendlyMessage = "Brak dostępu do kamery lub mikrofonu.";
      if (err.name === 'NotAllowedError') friendlyMessage = "Odmowa dostępu: Zezwól na używanie kamery/mikrofonu w przeglądarce.";
      else if (err.name === 'NotFoundError') friendlyMessage = "Błąd sprzętowy: Nie znaleziono podłączonej kamery lub mikrofonu.";
      else if (err.name === 'NotReadableError') friendlyMessage = "Błąd sprzętowy: Kamera jest obecnie używana przez inną aplikację.";
      
      logErrorToast(`[WebRTC] ${friendlyMessage}`, err);
      cleanup();
    }
  };

  useEffect(() => {
    const handleOffer = async (e: any) => {
      try {
        if (!e.detail) throw new Error("Otrzymano zdarzenie webRtcOffer bez danych (e.detail jest puste).");
        const { initiatorId, session } = e.detail as IWebRTCData;

        let stream = activeLocalStream.current;
        if (!stream) {
          // TUTAJ NASTĘPUJE OPÓŹNIENIE - użytkownik klika zezwól na kamerę
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          activeLocalStream.current = stream;
          setContext(prev => ({ ...prev, localStream: stream }));
        }

        let peer = pc.current;
        if (!peer || peer.signalingState === 'closed') {
          peer = initPeerConnection(initiatorId);
          stream.getTracks().forEach(track => peer!.addTrack(track, stream!));
        }

        if (!session) throw new Error("Brak danych SDP (offer) w zdarzeniu.");

        await peer.setRemoteDescription(new RTCSessionDescription(session));

        // WRZUCAMY KANDYDATÓW KTÓRZY CZEKALI W KOLEJCE (PRZYSZLI ZA WCZEŚNIE)
        const candidates = [...pendingCandidates.current];
        pendingCandidates.current = [];
        for (const cand of candidates) {
          try { 
            await peer.addIceCandidate(new RTCIceCandidate(cand)); 
          } catch (err) { 
            logErrorToast("[WebRTC] Błąd dodawania oczekującego kandydata ICE (w handleOffer):", err);
          }
        }

        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        emitSignal('webRtcAnswer', initiatorId, answer);
      } catch (err: any) {
        let prefix = "[WebRTC] Błąd przetwarzania oferty (handleOffer):";
        if (err.name === 'NotAllowedError' || err.name === 'NotFoundError' || err.name === 'NotReadableError') {
          prefix = "[WebRTC] Błąd dostępu do kamery/mikrofonu podczas odbierania:";
        }
        logErrorToast(prefix, err);
        cleanup(); // Zabezpieczenie: czyścimy jeśli nie udało się odebrać
      }
    };

    const handleAnswer = async (e: any) => {
      try {
        if (!e.detail) throw new Error("Otrzymano zdarzenie webRtcAnswer bez danych (e.detail jest puste).");
        const { session } = e.detail as IWebRTCData;
        
        if (!session) throw new Error("Brak danych SDP (answer) w zdarzeniu.");

        if (pc.current && pc.current.signalingState !== 'closed') {
          await pc.current.setRemoteDescription(new RTCSessionDescription(session));

          // Wrzucamy oczekujących kandydatów dla dzwoniącego
          const candidates = [...pendingCandidates.current];
          pendingCandidates.current = [];
          for (const cand of candidates) {
            try { 
              await pc.current.addIceCandidate(new RTCIceCandidate(cand)); 
            } catch (err) { 
              logErrorToast("[WebRTC] Błąd dodawania oczekującego kandydata ICE (w handleAnswer):", err);
            }
          }
        } else {
          throw new Error("Otrzymano odpowiedź, ale połączenie (pc.current) nie istnieje lub jest zamknięte.");
        }
      } catch (err) { 
        logErrorToast("[WebRTC] Błąd przetwarzania odpowiedzi (handleAnswer):", err);
      }
    };

    const handleCandidate = async (e: any) => {
      try {
        if (!e.detail) throw new Error("Otrzymano zdarzenie webRtcCandidate bez danych.");
        const { session } = e.detail as IWebRTCData;
        
        if (!session) throw new Error("Otrzymano pustego kandydata ICE.");

        const candidateData = session as RTCIceCandidateInit;

        // Jeśli pc.current nie istnieje lub nie ma jeszcze RemoteDescription, odkładamy kandydata na później!
        if (!pc.current || pc.current.signalingState === 'closed' || !pc.current.remoteDescription) {
          pendingCandidates.current.push(candidateData);
        } else {
          await pc.current.addIceCandidate(new RTCIceCandidate(candidateData));
        }
      } catch (err) {
        logErrorToast("[WebRTC] Błąd dodawania kandydata ICE na żywo:", err);
      }
    };

    // Twarde sprzątanie wywołane z zewnątrz (np. przy wylogowaniu w useAuth.logout()).
    const handleForceCleanup = () => cleanup();

    window.addEventListener('webRtcOffer', handleOffer);
    window.addEventListener('webRtcAnswer', handleAnswer);
    window.addEventListener('webRtcCandidate', handleCandidate);
    window.addEventListener('forceCleanupWebRTC', handleForceCleanup);

    return () => {
      window.removeEventListener('webRtcOffer', handleOffer);
      window.removeEventListener('webRtcAnswer', handleAnswer);
      window.removeEventListener('webRtcCandidate', handleCandidate);
      window.removeEventListener('forceCleanupWebRTC', handleForceCleanup);
    };
  }, []);

  useEffect(() => {
    try {
      if (localVideoRef.current && context.localStream) {
        localVideoRef.current.srcObject = context.localStream;
      }
      if (remoteVideoRef.current && context.remoteStream) {
        remoteVideoRef.current.srcObject = context.remoteStream;
      }
    } catch (err) {
      logErrorToast("[WebRTC] Błąd przypisywania strumienia wideo do interfejsu:", err);
    }
  }, [context.localStream, context.remoteStream]);

  return { localVideoRef, remoteVideoRef, startCall, cleanup };
};